import { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    useTheme,
    useMediaQuery,
    Link,
    Stack
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ContactSupport as ContactIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ExternalLinkDialog from '../components/common/ExternalLinkDialog';
import logo from '../assets/img/logo.png';
import ContactPopup from '../components/popups/ContactPopup';

const faqData = [
    {
        question: "How do I sign the waiver?",
        answer: (
            <>
                By paying your administrative fees, you are signing the waiver. When checking out, you will see the waiver disclaimer.
            </>
        )
    },
    {
        question: "I've been to Total Turf Experience before. How do I access my existing account?",
        answer: (
            <>
                Log in to the administrative portal using your EZFacility credentials. If you don't have credentials, please visit the{' '}
                <Link 
                    href="/register"
                    sx={{ textTransform: 'none' }}
                >
                    registration page
                </Link>
                {' '}to get set up. After completing the registration, you can{' '}
                <Link 
                    href="/login"
                    sx={{ textTransform: 'none' }}
                >
                    log in here
                </Link>
                .
            </>
        )
    },
    {
        question: "I'm new to Total Turf Experience. How do I create an account?",
        answer: (
            <>
                Please visit the{' '}
                <Link 
                    href="/register"
                    sx={{ textTransform: 'none' }}
                >
                    registration page
                </Link>
                {' '}to get set up. After completing the registration, you can{' '}
                <Link 
                    href="/login"
                    sx={{ textTransform: 'none' }}
                >
                    log in here
                </Link>
                .
            </>
        )
    },
    {
        question: "How do I add a child to my account?",
        answer: (
            <>
                <Typography paragraph>
                    To add a child to your account:
                </Typography>
                <ol>
                    <li>Click on "Children" in the sidebar menu</li>
                    <li>Click the "Add Child" button</li>
                    <li>Enter your child's first name, last name, date of birth, and gender</li>
                    <li>If your child is found in our system, click "Yes, this is my child" to link them to your account</li>
                    <li>If your child is not found, they will be created and linked to your account</li>
                </ol>
                <Typography paragraph>
                    Please note:
                </Typography>
                <ul>
                    <li>Changes may take up to 1 minute to appear in your account</li>
                    <li>If you need to remove a child from your account or if any information is incorrect, please contact TTE</li>
                    <li>Each child account requires a player administrative fee. For example, if you have 2 children, select 2 Players when paying administrative fees</li>
                </ul>
            </>
        )
    },
    {
        question: "What is the Entry Pass?",
        answer: "The Entry Pass is your digital check-in pass. Show this to staff when entering the facility. It automatically checks you in when displayed."
    },
    {
        question: "What are administration fees?",
        answer: "Administration fees are a small fee charged to cover the cost of processing your health waiver. This small fee is only charged once per year."
    },
    {
        question: "How do I view my check-in history?",
        answer: "After logging in, navigate to the Check-in History page from the dashboard menu to view your facility visits."
    },
    {
        question: "I forgot my password. What should I do?",
        answer: "Click the 'Forgot Password' link on the login page. You'll be redirected to EZFacility's password reset page."
    },
    {
        question: "How do I update my profile information?",
        answer: "Log in to your account and click on the Profile section. For certain changes, you may need to visit the front desk."
    }
];

const FAQ = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [externalUrl, setExternalUrl] = useState('');
    const [externalTitle, setExternalTitle] = useState('');

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    useEffect(() => {
        const handleExternalLink = (event) => {
            setExternalUrl(event.detail.url);
            setExternalTitle(event.detail.title);
            setDialogOpen(true);
        };

        window.addEventListener('openExternalLink', handleExternalLink);
        return () => window.removeEventListener('openExternalLink', handleExternalLink);
    }, []);

    return (
        <Box 
            sx={{ 
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                pb: 6
            }}
        >
            <Container maxWidth="md">
                <Box 
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pt: isMobile ? 4 : 8,
                        mb: 6
                    }}
                >
                    <img 
                        src={logo} 
                        alt="Total Turf Experience"
                        style={{ 
                            width: isMobile ? '150px' : '200px',
                            marginBottom: '2rem',
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/')}
                    />
                    
                    <Typography
                        variant={isMobile ? 'h4' : 'h3'}
                        component="h1"
                        align="center"
                        color="primary"
                        gutterBottom
                        sx={{ fontWeight: 'bold', mb: 4 }}
                    >
                        Frequently Asked Questions
                    </Typography>
                </Box>

                <Box sx={{ mb: 4 }}>
                    {faqData.map((faq, index) => (
                        <Accordion
                            key={index}
                            expanded={expanded === `panel${index}`}
                            onChange={handleChange(`panel${index}`)}
                            sx={{ mb: 1 }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${index}bh-content`}
                                id={`panel${index}bh-header`}
                            >
                                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                                    {faq.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography color="text.secondary">
                                    {faq.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/login')}
                        sx={{ minWidth: 200, py: 1.5 }}
                    >
                        Sign In
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setContactDialogOpen(true)}
                        startIcon={<ContactIcon />}
                        sx={{ minWidth: 200, py: 1.5 }}
                    >
                        Contact TTE
                    </Button>
                </Box>
            </Container>

            <ExternalLinkDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                url={externalUrl}
                title={externalTitle}
            />

            <ContactPopup
                open={contactDialogOpen}
                onClose={() => setContactDialogOpen(false)}
            />
        </Box>
    );
};

export default FAQ; 